import type { CashUp, Drawer, EodClose, KhumoState } from "./data";

export const todayKey = (d = new Date()) => d.toISOString().slice(0, 10);

export type EodCheck = {
  key: string;
  label: string;
  ok: boolean;
  detail: string;
};

export type EodSummary = {
  date: string;
  sales: number;
  cashTakings: number;
  cardTakings: number;
  floatIssued: number;
  pickups: number;
  counted: number;
  expected: number;
  variance: number;
  holding: number;
  tills: number;
  tillsCashedUp: number;
  checks: EodCheck[];
  ready: boolean;
};

const sum = (ns: number[]) => Math.round(ns.reduce((a, b) => a + b, 0) * 100) / 100;

/** Builds the reconciliation figures behind the End-of-Day report. */
export function buildEodSummary(s: KhumoState, date = todayKey()): EodSummary {
  const drawers: Drawer[] = s.drawers ?? [];
  const cashups: CashUp[] = s.cashups ?? [];
  const cashTakings = sum(drawers.map((d) => d.sales));
  const floatIssued = sum(drawers.map((d) => d.issued));
  const pickups = sum(drawers.map((d) => d.pickups));
  const counted = sum(cashups.map((c) => c.counted));
  const expected = sum(cashups.map((c) => c.expected));
  const variance = Math.round((counted - expected) * 100) / 100;
  const cardTakings = sum((s.terminals ?? []).map((t) => t.takings ?? 0));
  const tillsCashedUp = drawers.filter((d) => d.cashedUp).length;

  const checks: EodCheck[] = [
    {
      key: "tills",
      label: "Every till cashed up",
      ok: drawers.length > 0 && tillsCashedUp === drawers.length,
      detail: `${tillsCashedUp} of ${drawers.length} tills posted a shift cash-up`,
    },
    {
      key: "variance",
      label: "Cash variance explained",
      ok: Math.abs(variance) < 50,
      detail:
        Math.abs(variance) < 50
          ? "Counted cash agrees with expected takings"
          : "Variance of R" + Math.abs(variance).toFixed(2) + " needs a reason before closing",
    },
    {
      key: "holding",
      label: "Pickups swept to holding",
      ok: pickups === 0 || (s.holding ?? 0) > 0,
      detail:
        pickups === 0
          ? "No pickups were made today"
          : "Pickup / cash-up holding sits at R" + (s.holding ?? 0).toFixed(2),
    },
    {
      key: "cardq",
      label: "No card payments stuck offline",
      ok: (s.cardTxns ?? []).every((t) => t.status !== "queued"),
      detail: (s.cardTxns ?? []).some((t) => t.status === "queued")
        ? "Retry queued card payments before closing the day"
        : "All card-machine payments settled or declined",
    },
  ];

  return {
    date,
    sales: s.salesToday ?? 0,
    cashTakings,
    cardTakings,
    floatIssued,
    pickups,
    counted,
    expected,
    variance,
    holding: s.holding ?? 0,
    tills: drawers.length,
    tillsCashedUp,
    checks,
    ready: checks.every((c) => c.ok),
  };
}

/** True when the previous trading day has been closed off. */
export function isDayClosed(closes: EodClose[], date = todayKey()) {
  return closes.some((c) => c.date === date);
}

/** Roles that may keep working while an End-of-Day is outstanding. */
export const EOD_EXEMPT_ROLES = ["Owner", "Admin", "Manager", "Supervisor"];

export function eodBlocksRole(role: string) {
  return !EOD_EXEMPT_ROLES.includes(role);
}
